import React from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Collapse,
  Divider,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const SGMBuilder = ({
  sgmSelections,
  sgmBuilderOpen,
  toggleSGMBuilder,
  clearSGMBuilder,
  removeFromSGMBuilder,
  combinedOdds,
  eventDateTime,
  eventName,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Copy SGM to clipboard
  const copySGMToClipboard = () => {
    if (sgmSelections.length === 0) return;

    // Get the first selection's bookie
    const firstBookie = sgmSelections[0]?.bookie || "";

    // Format the date (assuming we have access to eventDateTime via props)
    const eventDate = new Date(eventDateTime);
    const formattedDate =
      eventDate instanceof Date && !isNaN(eventDate)
        ? `${eventDate.getDate().toString().padStart(2, "0")}/${(
            eventDate.getMonth() + 1
          )
            .toString()
            .padStart(2, "0")}/${eventDate.getFullYear()}`
        : "";

    // Format the selections for display in Excel
    const selectionsText = sgmSelections
      .map((selection) => `${selection.selection} - ${selection.market}`)
      .join(", ");

    // Build the tab-separated string for Excel
    const clipboardText = [
      formattedDate, // Date in dd/mm/yyyy format
      firstBookie, // Bookie of first bet
      "Qualifying", // Fixed text
      "", // Empty tab
      eventName || "", // Event name
      `SGM (${selectionsText})`, // SGM with selections
      "50", // Fixed value
    ].join("\t");

    // Copy to clipboard
    navigator.clipboard.writeText(clipboardText);
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        position: "relative",
        width: isMobile ? "100%" : sgmSelections.length > 0 ? "auto" : "350px",
        maxWidth: isMobile ? "100%" : "calc(100% - 16px)",
        maxHeight: isMobile ? "none" : "500px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        flexGrow: sgmSelections.length > 0 && !isMobile ? 1 : 0,
      }}
    >
      {/* Builder header with toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: sgmBuilderOpen ? 2 : 0,
          cursor: "pointer",
        }}
        onClick={toggleSGMBuilder}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ShoppingCartIcon
            sx={{
              mr: 1,
              color: sgmSelections.length ? "primary.main" : "inherit",
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            SGM Builder
          </Typography>
          {sgmSelections.length > 0 && (
            <Chip
              size="small"
              label={sgmSelections.length}
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        {sgmBuilderOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      <Collapse in={sgmBuilderOpen} sx={{ overflow: "auto" }}>
        {sgmSelections.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
              opacity: 0.7,
            }}
          >
            <AddCircleOutlineIcon sx={{ fontSize: 40, mb: 2 }} />
            <Typography variant="body1" align="center">
              Add selections to build your Same Game Multi
            </Typography>
            <Typography
              variant="body2"
              align="center"
              color="textSecondary"
              sx={{ mt: 1 }}
            >
              Use the SGM column to add selections
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                flexWrap: isMobile ? "nowrap" : "wrap",
                gap: 1,
                overflow: "auto",
              }}
            >
              {sgmSelections.map((selection) => (
                <Paper
                  key={selection.id}
                  elevation={0}
                  sx={{
                    width: isMobile ? "100%" : "auto",
                    minWidth: isMobile ? "100%" : "200px",
                    maxWidth: isMobile ? "100%" : "400px",
                    p: 1,
                    mb: isMobile ? 1 : 0,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    position: "relative",
                    flexGrow: 0,
                    flexShrink: 0,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => removeFromSGMBuilder(selection.id)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      color: theme.palette.grey[500],
                      padding: "2px",
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>

                  <Typography variant="body2" fontWeight="medium" noWrap>
                    {selection.selection}
                  </Typography>

                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                    noWrap
                  >
                    {selection.market}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    <Typography
                      variant="body2"
                      color="primary"
                      fontWeight="bold"
                    >
                      {selection.rate.toFixed(2)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ ml: 1 }}
                    >
                      {selection.bookie}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Combined Odds Display */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" fontWeight="bold" color="primary">
                Non-Correlated Odds: {combinedOdds.toFixed(2)}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  mt: 1,
                  display: "block",
                  fontStyle: "italic",
                  lineHeight: 1.3,
                }}
              >
                These are the odds assuming no correlation between the legs.
                Since this is an SGM, some correlation likely exists, so the
                bookmaker's actual odds will differ.
              </Typography>
            </Paper>

            {/* Action buttons */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={copySGMToClipboard}
                sx={{ flex: 1, maxWidth: 150 }}
              >
                Copy SGM
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearSGMBuilder}
                color="error"
                sx={{ flex: 1, maxWidth: 150 }}
              >
                Clear All
              </Button>
            </Box>
          </>
        )}
      </Collapse>
    </Paper>
  );
};

export default SGMBuilder;
